<script setup lang="ts">
import { ref } from 'vue';
import { useQuasar } from 'quasar';

import type { LevelDto, Locales } from '@/services/types/api.types';
import { useFormsStore } from '@/stores';
import { useI18n } from 'vue-i18n';

import MatrixRowEditDialog from '@/components/dialogs/MatrixRowEditDialog.vue';
import ReorderableTable from '@/components/ReorderableTable.vue';
import { getTranslation } from '@/composables/data-display-utils';

const $q = useQuasar();
const { t } = useI18n();

const formsStore = useFormsStore();

const props = defineProps<{
  rows: LevelDto[];
  formId: number;
  stepId: number;
  canChoicesBeDeleted?: boolean;
  languageEnabled: Locales[];
}>();

const columns = ref([
  {
    name: 'name',
    label: t('form.matrix-row.fields.name'),
    align: 'left',
    field: (row: LevelDto) => getTranslation(row, 'name', props.languageEnabled),
  },
]);

const handleRowClick = (row: LevelDto) => {
  $q.dialog({
    component: MatrixRowEditDialog,
    componentProps: {
      mode: 'edit',
      row,
      formId: props.formId,
      stepId: props.stepId,
      languageEnabled: props.languageEnabled,
    },
  });
};

const handleAddRow = () => {
  const row = formsStore.createMatrixRow();
  $q.dialog({
    component: MatrixRowEditDialog,
    componentProps: {
      mode: 'add',
      row,
      formId: props.formId,
      stepId: props.stepId,
      languageEnabled: props.languageEnabled,
    },
  });
};

const handleDeleteRow = (row: LevelDto) => {
  $q.dialog({
    title: t('form.dialogs.matrix-row.delete-title'),
    message: t('form.dialogs.matrix-row.delete-message'),
    ok: {
      label: t('global.yes'),
      color: 'negative',
      flat: true,
    },
    cancel: {
      label: t('global.no'),
      color: 'primary',
      unelevated: true,
    },
  }).onOk(() => {
    formsStore.deleteMatrixRow(row, props.formId, props.stepId);
  });
};

const handleReorder = async (source: LevelDto, target: LevelDto) => {
  await formsStore.saveMatrixRow(source, props.formId, props.stepId);
  await formsStore.saveMatrixRow(target, props.formId, props.stepId);
};
</script>

<template>
  <div>
    <reorderable-table
      :rows="props.rows"
      :columns="columns"
      :title="$t('form.matrix-row.list-title')"
      itemKey="id"
      :disable-delete-button="true"
      :action-menu-items="[
        {
          action: 'delete-row',
          label: t('global.label.delete'),
          disabled: !props.canChoicesBeDeleted,
          tooltip: !props.canChoicesBeDeleted ? t('global.notifications.choice-prohibit-delete') : undefined,
        },
      ]"
      @rowClick="handleRowClick"
      @reorder="handleReorder"
      @add-row="handleAddRow"
      @delete-row="handleDeleteRow"
    >
    </reorderable-table>
  </div>
</template>
